import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useFavorites } from '../../contexts/FavoritesContext';
import { getAllSections } from '../../utils/navigationHelpers';
import './FavoritesFlyout.css';

interface FavoritesFlyoutProps {
  open: boolean;
  onClose: () => void;
}

const FavoritesFlyout: React.FC<FavoritesFlyoutProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { favorites, isLoading, error, removeFavoriteById, getOrganizedFavorites, clearError } =
    useFavorites();

  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set());

  // Automatically expand all sections when flyout opens
  React.useEffect(() => {
    if (open && favorites.length > 0) {
      const sections = getAllSections(favorites);
      setExpandedSections(new Set(sections));
    }
  }, [open, favorites]);

  const handleSectionToggle = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handleNavigate = (route: string) => {
    navigate(route);
    onClose();
  };

  const handleRemoveFavorite = async (
    event: React.MouseEvent,
    favoriteId: number,
    favoriteName: string
  ) => {
    event.stopPropagation(); // Prevent navigation

    if (window.confirm(`Remove "${favoriteName}" from favorites?`)) {
      try {
        await removeFavoriteById(favoriteId);
      } catch (err) {
        // Error already handled by context
        console.error('Error removing favorite:', err);
      }
    }
  };

  const organizedFavorites = getOrganizedFavorites();
  const sections = Object.keys(organizedFavorites).sort();

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      className="favorites-flyout"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100vw',
          left: '240px', // Position next to navbar (navbar width = 240px)
          top: '64px', // Position below the AppBar
          height: 'calc(100% - 64px)', // Full height minus AppBar
        },
      }}
    >
      <Box className="favorites-flyout-header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: '#ffd700' }} />
          <Typography variant="h6">My Favorites</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box className="favorites-flyout-content">
        {error && (
          <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && favorites.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              No favorites yet. Click the star icon next to any menu item to add it to your
              favorites.
            </Typography>
          </Box>
        )}

        {!isLoading && favorites.length > 0 && (
          <List>
            {sections.map((section) => {
              const sectionData = organizedFavorites[section];
              const isExpanded = expandedSections.has(section);

              return (
                <React.Fragment key={section}>
                  {/* Section Header */}
                  <ListItemButton onClick={() => handleSectionToggle(section)}>
                    <ListItemText
                      primary={section}
                      primaryTypographyProps={{
                        fontWeight: 'bold',
                        color: 'primary',
                      }}
                    />
                    <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                      {sectionData.items.length}
                    </Typography>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>

                  {/* Section Items */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {sectionData.items.map((favorite) => (
                        <ListItem
                          key={favorite.id}
                          disablePadding
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              size="small"
                              onClick={(e) => handleRemoveFavorite(e, favorite.id, favorite.name)}
                              sx={{
                                opacity: 0.6,
                                '&:hover': {
                                  opacity: 1,
                                  color: 'error.main',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemButton
                            sx={{ pl: 4 }}
                            onClick={() => handleNavigate(favorite.route)}
                          >
                            {favorite.icon && (
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <StarIcon sx={{ fontSize: 16, color: '#ffd700' }} />
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={favorite.name}
                              secondary={favorite.category || undefined}
                              primaryTypographyProps={{
                                variant: 'body2',
                              }}
                              secondaryTypographyProps={{
                                variant: 'caption',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>

                  <Divider />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default FavoritesFlyout;
